// dev/목업 모드에서 실제 adsbygoogle.js 대신 설치하는 mock AdSense SDK (JCG-2057).
//
// 기존 목업은 컴포넌트가 회색 박스를 "항상" 렌더해, 실제 AdSense의 push 경로를
// 우회했다. 그래서 positional 큐 동작·폭 확정 대기·조건부 렌더가 dev에서 실행되지
// 않아, 사이드바 한쪽만 노출 / 인터스티셜 빈 화면 같은 버그가 프로덕션에서만
// 드러났다.
//
// 이 mock SDK는 실제 AdSense의 핵심 동작을 흉내 내 dev에서 동일한 버그를 재현한다:
// 1. push({}) → 페이지 DOM 순서상 다음 미초기화 ins를 채운다(positional).
// 2. offsetWidth === 0이면 채우지 않는다(모달 width 확정 전 등 → availableWidth=0).
// 3. push가 ins 개수보다 부족하면 마지막 ins가 빈 채로 남고, 콘솔에 경고를 남긴다.
// 4. 충전 시 ins의 모든 조상에 인라인 `height:auto !important`(+ min-height 가진 조상엔
//    `min-height:0 !important`)를 주입한다(JCG-2244, production DOM 관측과 동일). 고정 높이
//    컨테이너(ShellFrame aside 등)가 격리 구조(스페이서+abs 패널) 없이 광고를 품으면
//    dev에서도 collapse가 보이도록 하기 위함.

interface MockAdsbygoogle {
  push: (config?: unknown) => number;
  loaded: boolean;
}

const STATUS_ATTR = "data-adsbygoogle-status";

function isDarkMode(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark")
  );
}

/** mock SDK가 ins를 채울 때 실제 광고 대신 표시할 "목업 크리에이티브"를 주입한다. */
function renderMockCreative(ins: HTMLElement): void {
  const slot = ins.getAttribute("data-ad-slot") ?? "(슬롯 없음)";
  const width = ins.offsetWidth;
  const height = ins.offsetHeight;
  const minHeight = Math.max(height, 50);
  // block 반응형 ins는 높이가 0일 수 있으므로 보이도록 최소 높이 부여
  ins.style.minHeight = `${minHeight}px`;

  const dark = isDarkMode();
  const border = dark ? "#4b5563" : "#9ca3af";
  const bg = dark ? "#1f2937" : "#f3f4f6";
  const fg = dark ? "#9ca3af" : "#6b7280";

  ins.innerHTML = "";
  const box = document.createElement("div");
  box.style.cssText = [
    "width:100%",
    `min-height:${minHeight}px`,
    "box-sizing:border-box",
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    "justify-content:center",
    "gap:2px",
    "text-align:center",
    "padding:8px",
    "font-size:11px",
    `color:${fg}`,
    `background:${bg}`,
    `border:2px dashed ${border}`,
  ].join(";");
  box.innerHTML =
    `<div style="font-weight:600">애드센스 목업</div>` +
    `<div>slot ${slot}</div>` +
    `<div>${width} × ${minHeight}</div>`;
  ins.appendChild(box);
}

/**
 * 실제 AdSense가 충전 후 수행하는 조상 인라인 스타일 주입을 재현한다(JCG-2244).
 * - ins 자신: `min-height:0 !important; height:auto !important`
 * - body 이전의 모든 조상: `height:auto !important` (+ computed min-height 가 0이 아니면
 *   `min-height:0 !important` 까지 — production 관측에서 min-height 가진 요소만 받았다)
 * 인라인 `!important` 라 stylesheet 로 못 이기므로, 조상의 고정 높이에 기대는 레이아웃은
 * 여기서 깨진다 — ShellFrame 의 스페이서+abs 패널 격리가 지키는 불변식의 dev 검증 장치.
 */
function injectAncestorHeightOverrides(ins: HTMLElement): void {
  ins.style.setProperty("min-height", "0px", "important");
  ins.style.setProperty("height", "auto", "important");
  let el = ins.parentElement;
  while (el && el !== document.body && el !== document.documentElement) {
    el.style.setProperty("height", "auto", "important");
    if (parseFloat(getComputedStyle(el).minHeight) > 0) {
      el.style.setProperty("min-height", "0px", "important");
    }
    el = el.parentElement;
  }
}

/** push 1회: DOM 순서상 다음 미초기화 ins를 찾아(실제 AdSense와 동일) 채운다. */
function fillNextInsSlot(): void {
  if (typeof document === "undefined") return;

  const inses = Array.from(
    document.querySelectorAll<HTMLElement>("ins.adsbygoogle"),
  );
  const target = inses.find((el) => !el.getAttribute(STATUS_ATTR));

  if (!target) {
    console.warn(
      "[AdSense Mock] push를 채울 미초기화 ins가 없습니다 (push 횟수 > ins 개수). " +
        "positional 큐 불균형 — 실제 환경이면 push가 부족한 만큼 광고가 비어 보입니다.",
    );
    return;
  }

  if (target.offsetWidth === 0) {
    // 실제 AdSense도 availableWidth=0이면 채우지 않는다. status를 붙이지 않아
    // 폭이 확정된 뒤 재시도(useAdSensePush의 rAF) 때 채워질 수 있게 둔다.
    console.warn(
      `[AdSense Mock] availableWidth=0 — 채우지 않음 (slot ${target.getAttribute("data-ad-slot")}). ` +
        "모달 width 확정 전이거나 숨겨진 ins일 수 있습니다.",
    );
    return;
  }

  target.setAttribute(STATUS_ATTR, "done");
  renderMockCreative(target);
  // 충전 직후 실제 AdSense 와 동일하게 조상 height 를 무력화(JCG-2244).
  // 목업 크리에이티브 박스는 자체 min-height(inner div)로 시각 높이를 유지한다.
  injectAncestorHeightOverrides(target);
}

/**
 * mock AdSense SDK를 window.adsbygoogle에 설치한다.
 * 실제 SDK처럼, 설치 전 큐에 쌓여 있던 push도 모두 처리한다.
 */
export function installAdSenseMock(): void {
  if (typeof window === "undefined") return;

  const w = window as Window & { __adsenseMockInstalled?: boolean };
  if (w.__adsenseMockInstalled) return;
  w.__adsenseMockInstalled = true;

  const queued = Array.isArray(window.adsbygoogle) ? window.adsbygoogle : [];

  const mock: MockAdsbygoogle = {
    push: () => {
      fillNextInsSlot();
      return 0;
    },
    loaded: true,
  };
  window.adsbygoogle = mock as unknown as unknown[];

  // hydration 이전(SDK 설치 전)에 큐된 push 처리
  queued.forEach(() => fillNextInsSlot());

  console.info(
    "[AdSense Mock] 설치 완료 — 실제 adsbygoogle.js 대신 동작합니다 (dev/목업 모드).",
  );
}
